import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { supabase } from '@/lib/supabase'
import { useNavProgress } from '@/composables/useNavProgress'
import { useAuthStore } from '@/stores/auth'

const routes: RouteRecordRaw[] = [
  // ── Auth pages (each page wraps itself in AuthLayout) ─────────────────
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/LoginPage.vue'),
    meta: { requiresGuest: true },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/pages/RegisterPage.vue'),
    meta: { requiresGuest: true },
  },
  {
    // Universal Supabase callback: email confirmation, password reset, MFA recovery
    path: '/auth/callback',
    name: 'auth-callback',
    component: () => import('@/pages/AuthCallbackPage.vue'),
  },
  {
    // Strava OAuth callback — backend redirects here after successful OAuth
    path: '/strava/callback',
    name: 'strava-callback',
    component: () => import('@/pages/StravaCallbackPage.vue'),
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/pages/ForgotPasswordPage.vue'),
    meta: { requiresGuest: true },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/pages/ResetPasswordPage.vue'),
  },
  {
    path: '/mfa/challenge',
    name: 'mfa-challenge',
    component: () => import('@/pages/MfaChallengePage.vue'),
  },
  {
    // Post-signup wizard (optional cyclist profile) — uses AuthLayout-like
    // standalone shell so the user is not distracted by the main app navigation.
    path: '/onboarding/profile',
    name: 'onboarding-profile',
    component: () => import('@/pages/OnboardingProfilePage.vue'),
    meta: { requiresAuth: true },
  },

  // ── Authenticated app pages (AppLayout wrapper) ───────────────────────
  {
    path: '/',
    component: () => import('@/layouts/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        redirect: '/dashboard',
      },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/pages/DashboardPage.vue'),
      },
      {
        path: 'garage',
        name: 'garage',
        component: () => import('@/pages/GaragePage.vue'),
      },
      {
        path: 'garage/add',
        name: 'add-bike',
        component: () => import('@/pages/AddBikePage.vue'),
      },
      {
        path: 'garage/:bikeId',
        name: 'bike-detail',
        component: () => import('@/pages/BikeDetailPage.vue'),
        props: true,
      },
      {
        path: 'rides',
        name: 'rides',
        component: () => import('@/pages/RidesPage.vue'),
      },
      {
        path: 'rides/prepare',
        name: 'prepare-ride',
        component: () => import('@/pages/PrepareRidePage.vue'),
      },
      {
        path: 'rides/add',
        name: 'add-ride',
        component: () => import('@/pages/AddRidePage.vue'),
      },
      {
        path: 'maintenance',
        name: 'maintenance',
        component: () => import('@/pages/MaintenanceLogPage.vue'),
      },
      {
        path: 'maintenance/add',
        name: 'add-maintenance',
        component: () => import('@/pages/AddMaintenanceLogPage.vue'),
      },
      {
        path: 'stock',
        name: 'stock',
        component: () => import('@/pages/StockPage.vue'),
      },
      {
        path: 'settings/profile',
        name: 'profile-settings',
        component: () => import('@/pages/ProfileSettingsPage.vue'),
      },
    ],
  },

  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

const navProgress = useNavProgress()

router.beforeEach(async (to) => {
  navProgress.start()

  const { data: { session } } = await supabase.auth.getSession()
  const isAuthenticated = !!session

  if (to.meta.requiresAuth && !isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  if (to.meta.requiresGuest && isAuthenticated) {
    return { name: 'dashboard' }
  }

  // Onboarding gate: only relevant for authenticated routes (excluding the
  // onboarding page itself and the auth callback which both need to land
  // before the gate can be evaluated).
  if (
    to.meta.requiresAuth &&
    isAuthenticated &&
    to.name !== 'onboarding-profile' &&
    to.name !== 'auth-callback'
  ) {
    const auth = useAuthStore()
    // Awaits any concurrent profile creation triggered by onAuthStateChange,
    // so the redirect decision is based on the freshly-created server record.
    await auth.ensureProfileLoaded()
    if (auth.needsOnboarding) {
      return { name: 'onboarding-profile' }
    }
  }

  // Re-entering the wizard once completed bounces back to the dashboard;
  // the user edits these values via /settings/profile from now on.
  if (to.name === 'onboarding-profile' && isAuthenticated) {
    const auth = useAuthStore()
    await auth.ensureProfileLoaded()
    if (auth.user && auth.user.onboarding_completed_at) {
      return { name: 'dashboard' }
    }
  }
})

router.afterEach(() => {
  navProgress.done()
})
